import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const MeetKalinda = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-20 bg-navy-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="relative overflow-hidden rounded-lg shadow-2xl bg-navy-900">
                <img
                  src="https://customer-assets.emergentagent.com/job_f99a18a4-e930-4176-ac10-f2e206aadd3b/artifacts/ky6tl65b_IMG_6081.jpeg"
                  alt="Kalinda Washington with supporters at campaign event"
                  className="w-full h-auto object-cover"
                />
              </div>
              <div>
                <h1 className="text-5xl lg:text-6xl font-bold mb-6 text-white leading-tight">
                  Meet KALINDA WASHINGTON
                </h1>
                <p className="text-2xl text-white font-semibold mb-8 uppercase tracking-wide">
                  Principles. Integrity. Results.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Kalinda */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-navy-900 mb-8 text-center">WHY KALINDA</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-gray-50">
                <CardContent className="p-6">
                  <CheckCircle className="w-8 h-8 text-red-600 mb-3" />
                  <p className="text-lg text-gray-800 font-semibold">Classroom experience, not Albany talking points</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-50">
                <CardContent className="p-6">
                  <CheckCircle className="w-8 h-8 text-red-600 mb-3" />
                  <p className="text-lg text-gray-800 font-semibold">Proven leadership in real schools</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-50">
                <CardContent className="p-6">
                  <CheckCircle className="w-8 h-8 text-red-600 mb-3" />
                  <p className="text-lg text-gray-800 font-semibold">Focused on results for students and families</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-50">
                <CardContent className="p-6">
                  <CheckCircle className="w-8 h-8 text-red-600 mb-3" />
                  <p className="text-lg text-gray-800 font-semibold">Committed to strong schools, safe neighborhoods, and affordability</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Policy Sections */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto space-y-12">
            
            {/* Supporting School Choice */}
            <div className="bg-white rounded-lg shadow-md p-8 border-l-4 border-red-600">
              <h3 className="text-3xl font-bold text-navy-900 mb-6">SUPPORTING SCHOOL CHOICE</h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-lg text-gray-800">Empower parents to choose the best schools</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-lg text-gray-800">Expand high-quality charter schools</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-lg text-gray-800">Improve public schools & raise standards</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-lg text-gray-800">Focus on literacy & career readiness</p>
                </div>
              </div>
            </div>

            {/* Fighting for Affordability */}
            <div className="bg-white rounded-lg shadow-md p-8 border-l-4 border-red-600">
              <h3 className="text-3xl font-bold text-navy-900 mb-6">FIGHTING FOR AFFORDABILITY</h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-lg text-gray-800">Cut taxes and lower cost of living</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-lg text-gray-800">Reduce energy bills & gas prices</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-lg text-gray-800">Slash burdensome red tape</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-lg text-gray-800">Help small businesses thrive</p>
                </div>
              </div>
            </div>

            {/* Safe Communities */}
            <div className="bg-white rounded-lg shadow-md p-8 border-l-4 border-red-600">
              <h3 className="text-3xl font-bold text-navy-900 mb-6">SAFE COMMUNITIES</h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-lg text-gray-800">Back our police & fight crime</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-lg text-gray-800">Get tough on repeat offenders</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-lg text-gray-800">Invest in mental health support</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-lg text-gray-800">Stand up for victims' rights</p>
                </div>
              </div>
            </div>

            {/* Mental Health & Recovery */}
            <div className="bg-white rounded-lg shadow-md p-8 border-l-4 border-red-600">
              <h3 className="text-3xl font-bold text-navy-900 mb-6">MENTAL HEALTH & RECOVERY</h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-lg text-gray-800">Expand access to mental health care</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-lg text-gray-800">Boost addiction treatment programs</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-lg text-gray-800">Support veterans & at-risk youth</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-lg text-gray-800">Treat addiction as a health issue</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-red-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Fight for New York's Future</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            With experience, expertise, and a heart for service, Kalinda Washington is ready to bring 
            real solutions to Albany.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://buy.stripe.com/4gMeVec2v4oP6XP3sQ6g803" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 font-bold px-12 py-6 text-xl">
                Donate Now
              </Button>
            </a>
            <Link to="/volunteer">
              <Button size="lg" className="bg-navy-900 hover:bg-navy-900/90 text-white font-bold px-12 py-6 text-xl">
                Get Involved
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MeetKalinda;
